import React, { createContext, useState, useEffect } from "react";
import { navigate } from "gatsby";
import { jwtDecode } from "jwt-decode";
import { setSessionStorage, removeSessionStorage, fetchAPIwToken, mergeSimilarLogEntries } from "@utils/helper";

const apiBaseUrl = process.env.GATSBY_API_BASE_URL;

const defaultState = {
  token: null,
  showModal: false,
  setShowModal: () => {},
  exitUrl: null,
  setExitUrl: () => {},
  openExitModal: () => {},
  closeExitModal: () => {},
  modalType: null,
  setModalType: () => {},
  logoutTimer: null,
  isSidebarOpen: false,
  setIsSidebarOpen: () => {},
};

const AppContext = createContext(defaultState);

/**
 * Global application context provider for authentication and UI state
 * @component
 * @param {Object} props
 * @param {ReactNode} props.children - Child components
 */
const AppProvider = ({ children }) => {

  const [token, setToken] = useState(defaultState.token);
  const [userData, setUserData] = useState(null);
  const [showModal, setShowModal] = useState(defaultState.showModal);
  const [exitUrl, setExitUrl] = useState(defaultState.exitUrl);
  const [modalType, setModalType] = useState(defaultState.modalType);
  const [logoutTimer, setLogoutTimer] = useState(defaultState.logoutTimer);
  const [isSidebarOpen, setIsSidebarOpen] = useState(defaultState.isSidebarOpen);
  const [currParticipant, setCurrParticipant] = useState(null);
  const [participants, setParticipants] = useState(null);
  const [portalUsers, setPortalUsers] = useState(null);
  const [sessions, setSessions] = useState(null);
  const [territoryList, setTerritoryList] = useState(null);
  const [territoryListLoading, setTerritoryListLoading] = useState(false);
  const [territoryListError, setTerritoryListError] = useState(null);

  const [zipsList, setZipsList] = useState(null);
  const [zipsLoading, setZipsLoading] = useState(false);
  const [zipsError, setZipsError] = useState(null);
  const [zipsSearchList, setZipsSearchList] = useState(null);

  const [appLogLoading, setAppLogLoading] = useState(false);
  const [appLogList, setAppLogList] = useState(null);

  // Rep editing states
  const [repsList, setRepsList] = useState(null);
  const [editingTerritory, setEditingTerritory] = useState(null);
  const [territoryErrors, setTerritoryErrors] = useState(null);
  const [recentlyEditedTerritoryId, setRecentlyEditedTerritoryId] = useState(null);

  const fetchAppLogList = async (startDate = "2025-10-12%2012%3A00", endDate = null) => {
    setAppLogLoading(true);
    const result = await fetchAPIwToken().get(`${apiBaseUrl}/healthcheck/log/applog?start_date=${startDate}` + (endDate ? `&end_date=${endDate}` : ""), token);
    if (result?.success && result.response.status >= 200 && result.response.status < 300 && result.response.body.data ) {
      const filteredData = result.response.body.data
      .filter(item => (item.code.toLowerCase() === "updated zip" || item.code.toLowerCase() === "edited rep") && item.created)
      .map(item => {
        return {
          ...item,
          created: new Date(item.created).toLocaleString(),
        }
      })
      .sort((a, b) => new Date(b.created) - new Date(a.created));
      
      // Merge similar entries
      const mergedData = mergeSimilarLogEntries(filteredData);
      
      setAppLogList(mergedData);
      setAppLogLoading(false);
    }
  }


  const fetchZipsList = async (start = 0, limit = 100, search = null) => {
    try {
      setZipsLoading(true);
      let queryParams = `?offset=${start}&limit=${limit}`;
      if (search) {
        queryParams += `&r=${search}`;
      }
      const result = await fetchAPIwToken().get(`${apiBaseUrl}/zip/list${queryParams}`, token);
      if (result?.success && result.response.status >= 200 && result.response.status < 300 && result.response.body.data ) {
        if (search) {
          setZipsSearchList(result.response.body.data);
        } else {
          setZipsList(result.response.body.data);
        }
        setZipsError(null);
      } else {
        throw new Error("Failed to fetch zips list");
      }
    } catch (error) {
      console.error(error);
    }
    finally {
      setZipsLoading(false);
    }
  }

  const fetchTerritoryList = async () => {
    setTerritoryListLoading(true);
    setTerritoryListError(null);
    
    try {
      const result = await fetchAPIwToken().get(`${apiBaseUrl}/territory/list`, token);
      if (result?.success && result.response.status >= 200 && result.response.status < 300 && result.response.body.data ) {
        const data = result.response.body.data.map(item => {
          return {
            ...item,
            isTemp: !item.full_name,
            // replace all non numbers with empty string
            phone: item.phone && item.phone.trim().replaceAll(/[^0-9]/g, ""),
            director_phone: item.director_phone && item.director_phone.trim().replaceAll(/[^0-9]/g, ""),
          }
        });
        setTerritoryList(data);
        setTerritoryListError(null);
      } else {
        throw new Error("Failed to fetch territory list");
      }
    } catch (error) {
      console.error("Error fetching territory list:", error);
      setTerritoryListError(error.message);
    } finally {
      setTerritoryListLoading(false);
    }
  }

  const fetchRepsList = async () => {
    console.log("fetching reps list");
    try {
      const result = await fetchAPIwToken().get(`${apiBaseUrl}/rep/list`, token);
      if (result?.success && result.response.status >= 200 && result.response.status < 300 && result.response.body.data ) {
        const data = result.response.body.data.sort((a, b) => a.full_name.localeCompare(b.full_name));
        setRepsList(data);
      } else {
        throw new Error("Failed to fetch reps list");
      }
    } catch (error) {
      console.error("Error fetching reps list", error);
    }
  }

  const openExitModal = (url) => {
    setExitUrl(url);
    setShowModal(true);
  };

  const closeExitModal = () => {
    setShowModal(false);
    setTimeout(() => {
      setExitUrl("");
    }, 100);
  };

  /**
   * Starts the logout timer based on token expiration
   * @param {string} token - JWT token
   */
  const startLogoutTimer = (token) => {
    if (token) {
      clearLogoutTimer();
      const decoded = jwtDecode(token);
      if (decoded && decoded.expiry) {
        setUserData(decoded);
        const expirationTime = new Date(decoded.expiry).getTime();
        const timer = setTimeout(() => {
          console.log("Logout timer expired");
          // Perform logout actions here, like clearing token and redirecting to login page
          setToken(null);
          removeSessionStorage("dt_token");
          navigate("/");
        }, expirationTime - new Date().getTime());
        setLogoutTimer(timer);
      }
    }
  };

  const clearLogoutTimer = () => {
    clearTimeout(logoutTimer);
    setLogoutTimer(null);
  };

  /**
   * Resets the authentication token and updates related states
   * @param {string} token - New JWT token
   */
  const resetToken = (token) => {
    setToken(token);
    startLogoutTimer(token);
    setSessionStorage("dt_token", token);
  };

  const store = {
    token,
    setToken,
    userData,
    setUserData,
    showModal,
    setShowModal,
    exitUrl,
    setExitUrl,
    openExitModal,
    closeExitModal,
    modalType,
    setModalType,
    logoutTimer,
    setLogoutTimer,
    startLogoutTimer,
    clearLogoutTimer,
    resetToken,
    isSidebarOpen,
    setIsSidebarOpen,
    currParticipant,
    setCurrParticipant,
    participants,
    setParticipants,
    portalUsers,
    setPortalUsers,
    sessions,
    setSessions,

    territoryList,
    setTerritoryList,
    territoryListLoading,
    territoryListError,
    fetchTerritoryList,

    zipsList,
    zipsLoading,
    zipsError,
    fetchZipsList,
    zipsSearchList,

    appLogList,
    fetchAppLogList,
    appLogLoading,

    repsList,
    setRepsList,
    fetchRepsList,
    editingTerritory,
    setEditingTerritory,
    territoryErrors,
    setTerritoryErrors,
    recentlyEditedTerritoryId,
    setRecentlyEditedTerritoryId,
  };

  return <AppContext.Provider value={store}>{children}</AppContext.Provider>;
};

export default AppContext;

export { AppProvider };
